/**
 * `autoConfiguredFields` slice mutations — Multi-Add Phase 2.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global): the slice
 * primitive lives in `depot/types.ts::autoConfiguredFields`; this module
 * owns the pure functional mutations against an immer draft + read-only
 * accessors against a snapshot.
 *
 * Per `think-in-systems` (ai.knowledge/global): the slice is a primitive
 * — a per-device map of `fieldPath -> autoFilledValue`. Mutations are
 * thin functional shims so callers (`runBulkCommit` in `add/wiring.ts`'s
 * ONVIF-success branch, `<AutoConfiguredLabel>`'s user-edit eviction
 * effect) compose them via the depot's `produce((draft) => …)` boundary.
 * The helpers themselves are framework-agnostic — they can be unit-tested
 * by passing a hand-built draft with no zustand/React in scope.
 *
 * Why depot (not the add-flow's per-modal store): the add-flow store is
 * unmounted when the Multi-Add modal closes, but the settings panel
 * (`<AutoConfiguredLabel>` consumer) reads the slice when the user opens
 * Advanced Settings on a device that was just bulk-added. Crossing the
 * store boundary requires depot-level scope.
 *
 * Why session-only / no persistence: cross-session persistence is
 * intentionally deferred to a later phase (session-only scope decision)
 * — the slice resets on full page reload and that is acceptable for the
 * current phase. No localStorage / IndexedDB hook here.
 *
 * Exports:
 *   - `addAutoConfiguredField(deviceId, fieldPath, value)`
 *       — record an auto-fill at the point of population
 *   - `removeAutoConfiguredField(deviceId, fieldPath)`
 *       — evict when the user edits the field (label disappears)
 *   - `clearAutoConfiguredDevice(deviceId)`
 *       — drop the per-device map (e.g. device removed from room)
 *   - `isAutoConfigured(state, deviceId, fieldPath)`
 *       — read accessor for the label component's render guard
 *   - `getAutoConfiguredValue(state, deviceId, fieldPath)`
 *       — read accessor returning the originally-auto-filled value, so
 *         the label's eviction effect can compare against the live input
 *   - `isSensitiveAutoConfiguredFieldPath(fieldPath)`
 *       — predicate: paths that must never enter the depot (security)
 *   - `isUserInputFieldPath(fieldPath)`
 *       — predicate: paths that are user-typed, not auto-detected (UX)
 *
 * `fieldPath` is a free-form string ("auth.username", "auth.password",
 * "video.host", "manufacturer.brand", "manufacturer.model"…). Keeping it
 * stringly-typed at the slice layer matches the parallel `overrides`
 * primitive (a `DeepPartial<DeviceSetting>` keyed by string) and avoids
 * an enum churn whenever a new field becomes auto-configurable.
 */
import type { Device } from '@studio/models/device';
import type { Draft, State } from '../../depot/types.js';

/**
 * Predicate: should this field path be treated as security-sensitive?
 *
 * Per audit B2: the depot must never hold cleartext credentials
 * (passwords / secrets / tokens / API keys). Devtools or an accidental
 * log line would otherwise leak them. We match conservatively
 * — any path containing `password`, `secret`, `token`, `apiKey`, or
 * `passcode` (case-insensitive) skips the slice entirely. The "Auto
 * Configured" label simply does not render for those fields; that is
 * acceptable UX (the user can see the auto-filled value in the input
 * regardless, so the badge is informational not load-bearing).
 *
 * Why a single guard at the slice (not the call site): defense in depth.
 * Future callers that add new auto-fill paths will not accidentally leak
 * a sensitive value into the depot — the slice is the security boundary.
 */
export const isSensitiveAutoConfiguredFieldPath = (fieldPath: string): boolean =>
  /password|secret|token|apikey|passcode/i.test(fieldPath);

/**
 * Predicate: should this field path NEVER carry the "Auto Configured"
 * label because the user typed it (not auto-detected by discovery)?
 *
 * Per engineer feedback (auth.* must not carry the label): the entire
 * Authentication section (`auth.username`, `auth.password`, `auth.host`,
 * etc.) is user-input — the producer types these credentials in the auth
 * prompt that fires during the bulk-add ONVIF probe. The slice must NOT
 * record them so the settings panel's `<AutoConfiguredLabel>` does not
 * lie ("Auto Configured" when the user typed it).
 *
 * This is a UX distinction separate from the B2 sensitivity guard:
 *   - B2 keeps cleartext credentials out of the depot (security).
 *   - This keeps user-typed fields out of the auto-config slice (UX).
 *
 * The two overlap (`auth.password` is both) but the rationale is
 * different — keeping the predicates separate documents intent.
 */
export const isUserInputFieldPath = (fieldPath: string): boolean => /^auth\./i.test(fieldPath);

/**
 * Record a field as auto-configured for `deviceId` inside an immer draft.
 *
 * Idempotent on `(deviceId, fieldPath)`: a second call replaces the
 * stored value with the latest one (e.g. a re-probe that auto-fills the
 * same field with a fresher value should pin the label to that newer
 * value so the user-edit eviction comparison stays accurate).
 *
 * Per `code-comments-required` (ai.knowledge/global): immer draft is
 * mutated in-place; no return value.
 *
 * Per `think-in-systems` (ai.knowledge/global): the slice initialises a
 * per-device sub-map lazily so the depot's initial value (`{}`) stays
 * minimal.
 */
export const addAutoConfiguredField = (
  draft: Draft,
  deviceId: Device['id'],
  fieldPath: string,
  value: string
): void => {
  // Per audit B2: never store cleartext credentials in the depot. The
  // depot is exposed via Redux/zustand devtools and would surface in any
  // depot-state log line. Sensitive paths are dropped silently — the
  // <AutoConfiguredLabel> will not render for them, which is acceptable
  // (label is informational, not load-bearing).
  if (isSensitiveAutoConfiguredFieldPath(fieldPath)) return;
  // Per engineer feedback (auth.* must not carry the label): user-input
  // fields (the entire `auth.*` section) are typed by the producer, not
  // auto-detected. Recording them would render a misleading "Auto Configured" tag.
  if (isUserInputFieldPath(fieldPath)) return;
  const bucket = (draft.autoConfiguredFields[deviceId] ??= {});
  bucket[fieldPath] = value;
};

/**
 * Evict the auto-configured marker for `(deviceId, fieldPath)` — called
 * from the label component's eviction effect when the user edits the
 * field (live input value diverges from the stored auto-filled value).
 *
 * Empties the per-device sub-map when the last entry is removed so the
 * shape doesn't accumulate dead keys for devices the user fully
 * customised.
 *
 * No-op when either the device or the field is not in the slice.
 */
export const removeAutoConfiguredField = (
  draft: Draft,
  deviceId: Device['id'],
  fieldPath: string
): void => {
  const bucket = draft.autoConfiguredFields[deviceId];
  if (!bucket) return;
  delete bucket[fieldPath];
  if (Object.keys(bucket).length === 0) {
    delete draft.autoConfiguredFields[deviceId];
  }
};

/**
 * Drop the entire per-device sub-map. Used when a device is removed from
 * the room (the auto-configured markers are no longer addressable from
 * any UI) and from tests that want a clean fixture.
 *
 * No-op when the device is not in the slice.
 */
export const clearAutoConfiguredDevice = (draft: Draft, deviceId: Device['id']): void => {
  delete draft.autoConfiguredFields[deviceId];
};

/**
 * Read-only accessor — returns `true` iff the field is recorded as
 * auto-configured for the supplied device.
 *
 * Renderer hooks (`<AutoConfiguredLabel>`) call this through the depot
 * selector; the helper exists so the consumer code is free of slice-
 * shape coupling.
 */
export const isAutoConfigured = (
  state: State,
  deviceId: Device['id'],
  fieldPath: string
): boolean => state.autoConfiguredFields[deviceId]?.[fieldPath] !== undefined;

/**
 * Read-only accessor — returns the originally-auto-filled value for
 * `(deviceId, fieldPath)`, or `undefined` when the field is not (or is
 * no longer) auto-configured.
 *
 * The label component uses this in its user-edit eviction effect: when
 * the live input value differs from this returned value, the user has
 * edited the field and the entry must be evicted from the slice.
 */
export const getAutoConfiguredValue = (
  state: State,
  deviceId: Device['id'],
  fieldPath: string
): string | undefined => state.autoConfiguredFields[deviceId]?.[fieldPath];
